import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import MoviePaymentModal from './components/MoviePaymentModal';
import { movies, movieCategories, type Movie } from '../../mocks/movies';

function MovieCard({ movie, onWatch }: { movie: Movie; onWatch: (m: Movie) => void }) {
  const [hovered, setHovered] = useState(false);

  const typeLabel =
    movie.type === 'movie' ? 'فيلم' :
    movie.type === 'series' ? 'مسلسل' : 'رسوم متحركة';

  const typeIcon =
    movie.type === 'movie' ? 'ri-film-line' :
    movie.type === 'series' ? 'ri-tv-line' : 'ri-gamepad-line';

  return (
    <div
      className="relative rounded-xl overflow-hidden cursor-pointer group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Poster — links to detail page */}
      <Link to={`/movies/${movie.id}`} className="block">
        <div className="relative w-full" style={{ paddingBottom: '145%' }}>
          <img
            src={movie.poster}
            alt={movie.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

          {movie.badge && (
            <div className="absolute top-3 right-3 bg-[#C9A961] text-white text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
              {movie.badge}
            </div>
          )}

          <div className="absolute top-3 left-3 flex items-center gap-1 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded-md backdrop-blur-sm whitespace-nowrap">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={`${typeIcon} text-xs`} />
            </div>
            {typeLabel}
          </div>

          <div className="absolute bottom-16 left-3 bg-[#C9A961] text-white text-xs font-black px-2 py-0.5 rounded-full">
            25 د.إ
          </div>

          <div className="absolute bottom-0 right-0 left-0 p-4">
            <h3 className="text-white font-bold text-base leading-tight mb-1">{movie.title}</h3>
            <div className="flex items-center gap-3 text-xs text-gray-300">
              <span>{movie.year}</span>
              <span>•</span>
              <span>{movie.duration}</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-[#C9A961] text-xs" />
              </div>
              <span className="text-[#C9A961] text-sm font-bold">{movie.rating}</span>
            </div>
          </div>

          {/* Hover overlay */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col justify-center p-5 transition-opacity duration-300"
            style={{ opacity: hovered ? 1 : 0 }}
          >
            <span className="text-[#C9A961] text-xs font-semibold mb-2">{movie.genre.join(' • ')}</span>
            <h3 className="text-white font-bold text-lg mb-2">{movie.title}</h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3">{movie.description}</p>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/60 text-xs">{movie.language}</span>
              <span className="text-[#C9A961] font-black text-lg">25 <span className="text-sm">د.إ</span></span>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={e => { e.preventDefault(); onWatch(movie); }}
                className="bg-[#C9A961] hover:bg-[#b8922e] text-white text-sm font-black py-2.5 px-4 rounded-xl transition-colors flex items-center gap-2 justify-center cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-base" />
                </div>
                شاهد الآن — 25 د.إ
              </button>
              <span className="text-white/50 text-xs text-center">أو اضغط للتفاصيل</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function MoviesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'rating' | 'year'>('rating');
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);

  const filtered = useMemo(() => {
    let result = [...movies];

    if (searchQuery.trim()) {
      result = result.filter(m =>
        m.title.includes(searchQuery) ||
        m.genre.some(g => g.includes(searchQuery))
      );
    }

    if (activeCategory === 'movie') {
      result = result.filter(m => m.type === 'movie');
    } else if (activeCategory === 'series') {
      result = result.filter(m => m.type === 'series');
    } else if (activeCategory === 'animation') {
      result = result.filter(m => m.type === 'animation');
    } else if (activeCategory !== 'all') {
      const genreMap: Record<string, string> = {
        action: 'أكشن', drama: 'دراما', comedy: 'كوميدي',
        thriller: 'إثارة', romance: 'رومانسي',
      };
      const genreAr = genreMap[activeCategory];
      if (genreAr) result = result.filter(m => m.genre.includes(genreAr));
    }

    result.sort((a, b) =>
      sortBy === 'rating' ? b.rating - a.rating : b.year - a.year
    );
    return result;
  }, [activeCategory, searchQuery, sortBy]);

  const moviesCount    = movies.filter(m => m.type === 'movie').length;
  const seriesCount    = movies.filter(m => m.type === 'series').length;
  const animCount      = movies.filter(m => m.type === 'animation').length;

  return (
    <main dir="rtl">
      <Navbar />

      {/* Hero */}
      <div className="relative h-72 overflow-hidden mt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/b46cea7ff08b6ea43261e4244b1d0883.jpg"
          alt="أفلام ومسلسلات"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-white text-4xl font-bold mb-3">أفلام ومسلسلات ورسوم متحركة</h1>
          <p className="text-gray-300 text-base max-w-md">شاهد أفضل المحتوى بـ 25 درهم فقط لكل عمل</p>
          <div className="flex items-center gap-6 mt-5 text-sm flex-wrap justify-center">
            <div className="flex items-center gap-2 text-[#C9A961]">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-film-line" /></div>
              <span className="text-white">{moviesCount} فيلم</span>
            </div>
            <div className="w-px h-4 bg-white/30" />
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-tv-line text-[#C9A961]" /></div>
              <span className="text-white">{seriesCount} مسلسل</span>
            </div>
            <div className="w-px h-4 bg-white/30" />
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-gamepad-line text-[#C9A961]" /></div>
              <span className="text-white">{animCount} رسوم متحركة</span>
            </div>
          </div>

          {/* Price badge */}
          <div className="mt-4 inline-flex items-center gap-2 bg-[#C9A961]/20 border border-[#C9A961]/40 backdrop-blur-sm text-[#C9A961] font-black px-5 py-2 rounded-full text-sm">
            <i className="ri-price-tag-3-line" />
            25 درهم فقط لكل محتوى
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-8">
        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-base" />
            </div>
            <input
              type="text"
              placeholder="ابحث عن فيلم أو مسلسل أو رسوم متحركة..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pr-11 pl-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/10 transition-all"
            />
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sort-desc text-gray-400 text-sm" />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">ترتيب:</span>
            <button
              onClick={() => setSortBy('rating')}
              className={`text-xs font-semibold px-3 py-1 rounded-lg transition-all cursor-pointer whitespace-nowrap ${sortBy === 'rating' ? 'bg-[#C9A961] text-white' : 'text-gray-500 hover:text-[#C9A961]'}`}
            >التقييم</button>
            <button
              onClick={() => setSortBy('year')}
              className={`text-xs font-semibold px-3 py-1 rounded-lg transition-all cursor-pointer whitespace-nowrap ${sortBy === 'year' ? 'bg-[#C9A961] text-white' : 'text-gray-500 hover:text-[#C9A961]'}`}
            >الأحدث</button>
          </div>
        </div>

        {/* Category filters */}
        <div className="flex items-center gap-2 flex-wrap mb-8">
          {movieCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-[#C9A961] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#C9A961] hover:text-[#C9A961]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            يعرض <span className="font-bold text-gray-800">{filtered.length}</span> نتيجة
          </p>
          <div className="flex items-center gap-1.5 text-xs text-[#C9A961] font-bold">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-price-tag-3-line" />
            </div>
            25 درهم / محتوى
          </div>
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filtered.map(movie => (
              <MovieCard key={movie.id} movie={movie} onWatch={setSelectedMovie} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center mb-4">
              <i className="ri-film-line text-5xl text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-500 mb-2">لم نجد نتائج</h3>
            <p className="text-sm text-gray-400">جرّب البحث بكلمة مختلفة أو تغيير الفئة</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
              className="mt-4 text-[#C9A961] text-sm font-semibold hover:underline cursor-pointer"
            >
              مسح الفلاتر
            </button>
          </div>
        )}
      </div>

      <Footer />

      {/* Payment Modal */}
      {selectedMovie && (
        <MoviePaymentModal
          movie={selectedMovie}
          onClose={() => setSelectedMovie(null)}
        />
      )}
    </main>
  );
}
